import * as React from 'react';
import './eqAutocomplete.scss';
import usePlacesAutocomplete, { getGeocode, getLatLng, getZipCode, getDetails } from "use-places-autocomplete";
import EqButton from '../eqButton/EqButton';
import { Input } from 'antd';
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import { ILocationDto } from '../../shared/interfaces/ILocationDto';

interface EqAutocompleteProps {
    setSelected: (location: { lat: number; lng: number }) => void;
    setLocation: (location: ILocationDto) => void;
}

interface Suggestion {
    place_id: string;
    structured_formatting: {
        main_text: string;
        secondary_text: string;
    };
}

const EqAutocomplete: React.FC<EqAutocompleteProps> = ({ setSelected, setLocation }) => {
    const { t } = useTranslation();
    const [selectedPlace, setSelectedPlace] = React.useState<ILocationDto>({} as ILocationDto);
    
    const {
        ready,
        value,
        suggestions: { status, data },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        requestOptions: {},
        debounce: 300,
    });

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
    };

    const getPlaceDetails = (place_id: string, description: string, lat: number, lng: number) => {
        const parameter = { placeId: place_id };
        
        getDetails(parameter).then((details) => {
            if (typeof details === "string" || !details) {
                console.error("Invalid details received", details);
                return;
            }
    
            let zip = "";
            let city = "";
            let pic = "";
            let countryName = "";
    
            if (details.photos && details.photos.length > 0) {
                pic = details.photos[0].getUrl?.() || "";
            }
    
            details.address_components?.forEach((cmp) => {
                if (cmp.types.includes("postal_code")) zip = cmp.long_name;
                if (cmp.types.includes("administrative_area_level_1")) city = cmp.long_name;
                if (cmp.types.includes("country")) countryName = cmp.long_name;
            });
    
            setLocation({
                locationName: description,
                street: description,
                longitude: lng,
                latitude: lat,
                city,
                zipCode: zip,
                locationImageUrl: pic,
                countryName,
                key: 0,
                iSOTwoLetterCode: '',
                iSOThreeLetterCode: '',
                state: '',
                municipality: '',
                color: ''
            });
        }).catch((error) => console.error("Error fetching place details:", error));
    };
    

    const handleSelect = (suggestion: Suggestion) => () => {
        const { place_id, structured_formatting: { main_text } } = suggestion;
        setValue(main_text, false);
        clearSuggestions();

        getGeocode({ address: main_text }).then((results) => {
            const { lat, lng } = getLatLng(results[0]);
            setSelected({ lat, lng });
            getPlaceDetails(place_id, main_text, lat, lng);
        });
    };

    const renderSuggestions = () =>
        data.map((suggestion) => (
            <li key={suggestion.place_id} onClick={handleSelect(suggestion)}>
                <strong>{suggestion.structured_formatting.main_text}</strong> <small>{suggestion.structured_formatting.secondary_text}</small>
            </li>
        ));

    return (
        <div className="eq-autocomplete">
            <Input
                value={value}
                onChange={handleInput}
                disabled={!ready}
                placeholder={t("enterPlace")}
                className="eq-search-input"
                prefix={<SearchOutlined />}
            />
            <div className="eq-search-results">
                {status === "OK" && <ul>{renderSuggestions()}</ul>}
            </div>
        </div>
    );
};

export default EqAutocomplete;
